import React, { useState, useEffect } from 'react'
import { Container, Card, Alert, Spinner, Button, Row, Col } from 'react-bootstrap'
import d_log from 'loglevel'
import api_utils from '../lib/api_utils'

function Flagged() {
  const [flaggedItems, setFlaggedItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchFlaggedItems = async () => {
    setLoading(true)
    setError(null)
    try {
      d_log.debug('Fetching flagged items from backend')
      const flaggedUrl = `${import.meta.env.VITE_SERVICE_URL}/infer/rag-flagged`
      const headers = api_utils.form_auth_headers()
      const response = await api_utils.call_backend_url(flaggedUrl, headers)

      // Extract flagged items from the "flagged" property in the response
      const flaggedData = response?.flagged || []
      setFlaggedItems(flaggedData)
      d_log.debug('Flagged items loaded successfully:', flaggedData.length, 'items')
    } catch (err) {
      d_log.error('Failed to fetch flagged items:', err)
      setError(`Failed to load flagged items: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchFlaggedItems()
  }, [])

  const handleReload = () => {
    d_log.debug('User requested reload of flagged items')
    fetchFlaggedItems()
  }

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading flagged items...</span>
        </Spinner>
        <p className="mt-3">Loading flagged items...</p>
      </Container>
    )
  }

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2>Flagged Items</h2>
          <p className="text-muted mb-0">Questions and answers that have been flagged for review</p>
        </div>
        <Button
          variant="outline-primary"
          onClick={handleReload}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Reloading...
            </>
          ) : (
            <>
              🔄 Reload
            </>
          )}
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mt-3">
          <Alert.Heading>Error Loading Flagged Items</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}

      {flaggedItems.length === 0 ? (
        <Card className="mt-4">
          <Card.Body>
            <div className="text-center py-5">
              <div className="text-muted mb-3">
                <i className="fas fa-flag" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
              </div>
              <h5 className="text-muted">No Flagged Items</h5>
              <p className="text-muted">
                Questions and answers that are flagged for review will appear here.
              </p>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <div className="mt-4">
          {flaggedItems.map((item, index) => (
            <Card key={index} className="mb-4" style={{ border: '2px solid #ffc107' }}>
              <Card.Header className="bg-warning bg-opacity-10">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="fas fa-flag text-warning me-2"></i>
                    Flagged Item #{index + 1}
                  </h5>
                  <small className="text-muted">
                    {item.timestamp && new Date(item.timestamp).toLocaleString()}
                  </small>
                </div>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={12} className="mb-3">
                    <h6 className="text-primary mb-2">
                      <i className="fas fa-question-circle me-1"></i>
                      Question:
                    </h6>
                    <div className="p-3 bg-light rounded border-start border-primary border-3">
                      {item.question || 'No question provided'}
                    </div>
                  </Col>

                  <Col md={12} className="mb-3">
                    <h6 className="text-success mb-2">
                      <i className="fas fa-comment-dots me-1"></i>
                      Answer:
                    </h6>
                    <div className="p-3 bg-light rounded border-start border-success border-3">
                      {item.answer || 'No answer provided'}
                    </div>
                  </Col>

                  {item.notes && (
                    <Col md={12}>
                      <h6 className="text-info mb-2">
                        <i className="fas fa-sticky-note me-1"></i>
                        Notes:
                      </h6>
                      <div className="p-3 bg-light rounded border-start border-info border-3">
                        {item.notes}
                      </div>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}
    </Container>
  )
}

export default Flagged